import { Injectable, signal } from '@angular/core';

/**
 * Represents the visual type of a toast notification.
 * `success` is used for successful operations, while `error`
 * is used when an operation fails or an error needs to be shown.
 */
export type ToastType = 'success' | 'error';

/**
 * Represents the data required to render a toast notification.
 */
export interface ToastNotification {
  /**
   * Unique identifier assigned to the toast notification.
   */
  id: number;

  /**
   * Visual type of the toast notification.
   */
  type: ToastType;

  /**
   * Message displayed to the user.
   */
  message: string;

  /**
   * Indicates whether the toast is currently running its exit transition.
   */
  isExiting?: boolean;
}

/**
 * Shared service responsible for managing toast notifications.
 * Provides methods to display success and error messages, automatically
 * dismiss notifications after a fixed duration, and coordinate toast
 * transitions when a new notification is displayed while another toast
 * is still visible.
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  /**
   * Internal signal containing the currently displayed toast notification.
   * The signal is kept private so that toast state can only be modified
   * through the service methods.
   */
  private readonly notifications = signal<ToastNotification[]>([]);

  /**
   * Stores auto-dismiss timers for each toast using its unique identifier.
   * This allows timers to be cancelled when a toast is manually dismissed
   * or replaced by another notification.
   */
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  /**
   * Timer used to complete the toast exit transition.
   * The timer is cleared implicitly when the scheduled callback completes
   * and the reference is reset.
   */
  private exitTimer: ReturnType<typeof setTimeout> | undefined;
  /**
   * Stores a new toast notification while the currently displayed toast
   * is completing its exit transition.
   */
  private pendingToast: ToastNotification | null = null;
  /**
   * Identifier assigned to each newly created toast notification.
   */
  private nextId = 0;
  /**
   * Duration of the toast enter and exit transition in milliseconds.
   */
  private readonly transitionDuration = 250;

  /**
   * Read-only signal consumed by the toast component.
   * Exposes the current toast notification without allowing external
   * consumers to modify the internal signal directly.
   */
  public readonly toasts = this.notifications.asReadonly();

  /**
   * Displays a success toast notification.
   * @param message Message displayed to the user.
   */
  public showSuccess(message: string): void {
    this.show('success', message);
  }

  /**
   * Displays an error toast notification.
   * @param message Message displayed to the user.
   */
  public showError(message: string): void {
    this.show('error', message);
  }
  /**
   * Starts the exit transition for the selected toast.
   * Clears the toast's auto-dismiss timer and marks the current toast
   * as exiting before scheduling its removal.
   * @param id Unique identifier of the toast to dismiss.
   */
  public dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    const currentToast = this.notifications()[0];
    if (!currentToast || currentToast.id !== id) {
      return;
    }
    if (!currentToast.isExiting) {
      this.notifications.set([{ ...currentToast, isExiting: true }]);
    }
    this.scheduleExit(currentToast.id);
  }
  /**
   * Creates and displays a new toast notification.
   * If another toast is currently displayed, the existing toast is first
   * marked for exit and the new notification is stored as a pending toast.
   * The pending toast is displayed after the current toast finishes its
   * exit transition.
   * @param type Visual state of the new toast.
   * @param message Message displayed to the user.
   */
  private show(type: ToastType, message: string): void {
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();
    const nextToast: ToastNotification = { id: ++this.nextId, type, message };
    const currentToast = this.notifications()[0];
    if (currentToast) {
      this.pendingToast = nextToast;
      if (!currentToast.isExiting) {
        this.notifications.set([{ ...currentToast, isExiting: true }]);
      }
      this.scheduleExit(currentToast.id);
      return;
    }

    this.display(nextToast);
  }
  /**
   * Displays the specified toast notification and starts its
   * automatic dismissal timer.
   * @param toast Toast notification to display.
   */
  private display(toast: ToastNotification): void {
    this.notifications.set([toast]);
    this.timers.set(toast.id, setTimeout(() => this.dismiss(toast.id), 4000));
  }
  /**
   * Schedules removal of the current toast after its exit transition
   * has completed.
   * Once the current toast is removed, any pending toast is displayed.
   * @param id Identifier of the toast that is currently exiting.
   */
  private scheduleExit(id: number): void {
    if (this.exitTimer) {
      return;
    }
    this.exitTimer = setTimeout(() => {
      this.exitTimer = undefined;
      const currentToast = this.notifications()[0];
      if (!currentToast || currentToast.id !== id) {
        return;
      }
      this.notifications.set([]);
      if (this.pendingToast) {
        const nextToast = this.pendingToast;
        this.pendingToast = null;
        this.display(nextToast);
      }
    }, this.transitionDuration);
  }
}